import { createContext, useCallback, useContext, useMemo, useState } from "react";
import useMediaQuery from "./useMediaQuery";

export const LAYOUT_MODE_KEY = "mysolon.layoutMode";
export const LAYOUT_PREFS = ["auto", "desktop", "classic"];

// In "auto" the desktop layout kicks in at this width.
export const DESKTOP_AUTO_QUERY = "(min-width: 1280px)";
// Below this width desktop can never be forced, so mobile always stays classic.
export const DESKTOP_MIN_QUERY = "(min-width: 1024px)";

const readPref = () => {
  try {
    const value = localStorage.getItem(LAYOUT_MODE_KEY);
    return LAYOUT_PREFS.includes(value) ? value : "auto";
  } catch {
    return "auto";
  }
};

const LayoutModeContext = createContext({
  mode: "classic",
  isDesktop: false,
  canForceDesktop: false,
  pref: "auto",
  setPref: () => {},
});

export const LayoutModeProvider = ({ children }) => {
  const [pref, setPrefState] = useState(readPref);
  const isWide = useMediaQuery(DESKTOP_AUTO_QUERY);
  const canForceDesktop = useMediaQuery(DESKTOP_MIN_QUERY);

  const setPref = useCallback((value) => {
    if (!LAYOUT_PREFS.includes(value)) return;
    setPrefState(value);
    try {
      localStorage.setItem(LAYOUT_MODE_KEY, value);
    } catch {}
  }, []);

  const mode = useMemo(() => {
    if (!canForceDesktop || pref === "classic") return "classic";
    if (pref === "desktop") return "desktop";
    return isWide ? "desktop" : "classic";
  }, [canForceDesktop, pref, isWide]);

  const value = useMemo(
    () => ({
      mode,
      isDesktop: mode === "desktop",
      canForceDesktop,
      pref,
      setPref,
    }),
    [mode, canForceDesktop, pref, setPref]
  );

  return (
    <LayoutModeContext.Provider value={value}>
      {children}
    </LayoutModeContext.Provider>
  );
};

export const useLayoutMode = () => useContext(LayoutModeContext);

export default LayoutModeContext;
